import React, { useMemo } from 'react'
import { useCompanyDetailContextValue } from '../context/CompanyDetailProvider'
import { InfoTable } from 'components/InfoTable'
import { AccordionCard } from 'components/AccordionCard'
import {
  Calendar,
  EllipsisVertical,
  Lock,
  Pencil,
  Percent,
  ReceiptText,
  Users,
} from 'lucide-react'
import { formatPercentage } from 'lib/percentage'
import {
  Button,
  DropdownMenu,
  Flex,
  Message,
  Text,
  Tooltip,
  useAppTheme,
} from '@workwhile/ui'
import { useCompanyDetailWorkflowsContextValue } from '../context/CompanyDetailWorkflowProvider'
import { formatDuration } from 'date-fns'
import { useWhoamiContextValue } from 'components/Whoami'
import {
  getPermissionName,
  UserPermission,
} from 'api/admin/userPermissions/permissions'
import { CompanyInvoiceConfig } from 'api/company/companyDetail'

function formatInvoiceConfigScope(invoiceConfig: CompanyInvoiceConfig) {
  if (invoiceConfig.companyId != null) {
    return `Company`
  }

  if (invoiceConfig.companyGroupId != null) {
    return `Company group`
  }

  return 'Unknown'
}

function formatInvoiceConfigCycle(invoiceConfig: CompanyInvoiceConfig) {
  if (invoiceConfig.customWeek == null) {
    return 'Standard'
  }

  const weekStartDay = invoiceConfig.customWeek.weekStartDay
  const capitalizedWeekStartDay =
    weekStartDay.charAt(0).toUpperCase() + weekStartDay.slice(1)

  return `Weekly (${capitalizedWeekStartDay}, ${invoiceConfig.customWeek.timezone})`
}

function formatInvoiceConfigBreakdown(invoiceConfig: CompanyInvoiceConfig) {
  if (invoiceConfig.breakdownLevels.length === 0) {
    return 'None'
  }

  return invoiceConfig.breakdownLevels.join(', ')
}

type PermissionedDropdownMenuItemProps = {
  label: string
  requiredPermission: UserPermission
  enabled: boolean
  onSelect: () => void
}

const PermissionedDropdownMenuItem = ({
  label,
  requiredPermission,
  enabled,
  onSelect,
}: PermissionedDropdownMenuItemProps) => {
  const item = (
    <DropdownMenu.Item
      onSelect={(event) => {
        if (!enabled) {
          event.preventDefault()
          return
        }
        onSelect()
      }}
    >
      <Flex as="span" alignItems="center" style={{ gap: 8 }}>
        {enabled ? <Pencil size={14} /> : <Lock size={14} />}
        {label}
      </Flex>
    </DropdownMenu.Item>
  )

  if (enabled) {
    return item
  }

  return (
    <Tooltip
      content={`Requires ${getPermissionName(requiredPermission)} permission`}
      variant="neutral"
    >
      {item}
    </Tooltip>
  )
}

export const Money = () => {
  const { space } = useAppTheme()
  const {
    state: {
      type,
      takeRate1099,
      paymentMethod,
      bonusTakeRate,
      tipTakeRate,
      w2RegularTakeRate,
      w2DoubleTakeRate,
      w2OvertimeTakeRate,
      invoiceDueDays,
      invoiceConfig,
      shiftLeadChargePolicy,
      chargeCushionWorkers,
    },
  } = useCompanyDetailContextValue()

  const { editMoneyFlow, editPaymentMethodFlow } =
    useCompanyDetailWorkflowsContextValue()
  const { hasPermission } = useWhoamiContextValue()
  const canEditPaymentMethod = hasPermission('finance_specialist')

  const isOnInvoice = paymentMethod === 'invoice'
  const hasUnusedInvoiceConfig = invoiceConfig != null && !isOnInvoice

  const formattedPaymentMethod = useMemo(() => {
    if (!paymentMethod) {
      return 'Not set'
    }

    if (paymentMethod === 'invoice') {
      return 'Invoice'
    }

    if (paymentMethod === 'card') {
      return 'Card'
    }

    if (paymentMethod === 'bank_account') {
      return 'Bank'
    }

    return paymentMethod
  }, [paymentMethod])

  return (
    <AccordionCard
      title={
        <Flex
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          width="100%"
        >
          <Text fontSize={2} color="lightText">
            Money
          </Text>

          <DropdownMenu
            trigger={
              <Button
                variant="secondary"
                kind="small"
                style={{ paddingLeft: space[1], paddingRight: space[1] }}
              >
                <EllipsisVertical size={14} />
              </Button>
            }
          >
            <PermissionedDropdownMenuItem
              label="Edit payment method"
              requiredPermission="finance_specialist"
              enabled={canEditPaymentMethod}
              onSelect={editPaymentMethodFlow.open}
            />
            <DropdownMenu.Item onSelect={editMoneyFlow.open}>
              <Flex as="span" alignItems="center" style={{ gap: 8 }}>
                <Pencil size={14} />
                Edit take rates
              </Flex>
            </DropdownMenu.Item>
          </DropdownMenu>
        </Flex>
      }
      // Intentionally disabled. This should work, unsure why it's throwing an error
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      maxWidth="50%"
    >
      {hasUnusedInvoiceConfig ? (
        <Message variant="error" mb={3} borderRadius="small">
          Invalid payment method: company has an invoice config but payment
          method is not invoice.
        </Message>
      ) : null}

      <InfoTable>
        <InfoTable.Row
          icon={<ReceiptText size={14} />}
          label="Payment method"
          value={formattedPaymentMethod}
        />

        {isOnInvoice ? (
          <InfoTable.Row
            icon={<Calendar size={14} />}
            label="Invoice terms"
            value={formatDuration({ days: invoiceDueDays })}
          />
        ) : null}

        {invoiceConfig != null ? (
          <>
            <InfoTable.Row
              icon={<Calendar size={14} />}
              label="Invoice config scope"
              value={formatInvoiceConfigScope(invoiceConfig)}
            />
            <InfoTable.Row
              icon={<Calendar size={14} />}
              label="Invoice breakdown"
              value={formatInvoiceConfigBreakdown(invoiceConfig)}
            />
            <InfoTable.Row
              icon={<Calendar size={14} />}
              label="Invoice cycle"
              value={formatInvoiceConfigCycle(invoiceConfig)}
            />
          </>
        ) : null}

        <InfoTable.Row
          icon={<ReceiptText size={14} />}
          label="Shift lead charge policy"
          value={shiftLeadChargePolicy ?? 'Standard'}
        />

        {type === '1099' ? (
          <>
            <InfoTable.Row
              icon={<Percent size={14} />}
              label="Take rate"
              value={takeRate1099 ? formatPercentage(takeRate1099) : 'N/A'}
            />
          </>
        ) : null}

        <InfoTable.Row
          icon={<Percent size={14} />}
          label="Tip take rate"
          value={tipTakeRate ? formatPercentage(tipTakeRate) : 'N/A'}
        />

        <InfoTable.Row
          icon={<Percent size={14} />}
          label="Bonus take rate"
          value={bonusTakeRate ? formatPercentage(bonusTakeRate) : 'N/A'}
        />

        {type === 'w2' ? (
          <>
            <InfoTable.Row
              icon={<Percent size={14} />}
              label="W2 Regular take rate"
              value={
                w2RegularTakeRate ? formatPercentage(w2RegularTakeRate) : 'N/A'
              }
            />

            <InfoTable.Row
              icon={<Percent size={14} />}
              label="W2 Double take rate"
              value={
                w2DoubleTakeRate ? formatPercentage(w2DoubleTakeRate) : 'N/A'
              }
            />

            <InfoTable.Row
              icon={<Percent size={14} />}
              label="W2 Overtime take rate"
              value={
                w2OvertimeTakeRate
                  ? formatPercentage(w2OvertimeTakeRate)
                  : 'N/A'
              }
            />
          </>
        ) : null}

        <InfoTable.Row
          icon={<Users size={14} />}
          label="Charge cushioned workers"
          value={chargeCushionWorkers ? 'Yes' : 'No'}
        />
      </InfoTable>
    </AccordionCard>
  )
}
